import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export type TimelineKind = 'followups' | 'appointments';
export type ParentType = 'lead' | 'deal' | 'complaint' | string;

export interface TimelineRow {
  _id?: string;
  id?: string;
  status: string;
  note?: string;
  details?: string;
  createdAt?: string;
  followupDate?: string;
  appointmentDate?: string;
  parentType?: ParentType;
  parentId?: string;
  [key: string]: any;
}

export type Followup = TimelineRow;
export type Appointment = TimelineRow;

export function dateFieldFor(kind: TimelineKind): string {
  return kind === 'followups' ? 'followupDate' : 'appointmentDate';
}

export function dateOf(kind: TimelineKind, row: TimelineRow): string {
  return (kind === 'followups' ? row.followupDate : row.appointmentDate) || row.createdAt || '';
}

export function useTimelineList(kind: TimelineKind, parentType: ParentType, parentId?: string | null) {
  return useQuery({
    queryKey: ['timeline', kind, parentType, parentId],
    queryFn: async () => {
      if (!parentId) return [];
      try {
        const res = await api.get<TimelineRow[]>(`/api/v1/goodday/${kind}`, {
          params: { parentType, parentId },
        });
        return Array.isArray(res) ? res : (res as any)?.data || [];
      } catch {
        return [];
      }
    },
    enabled: Boolean(parentId),
  });
}

export function useCreateTimelineRow(kind: TimelineKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await api.post<TimelineRow>(`/api/v1/goodday/${kind}`, payload);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeline', kind] });
    },
  });
}

export function useUpdateTimelineRow(kind: TimelineKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; [key: string]: any }) => {
      const res = await api.patch<TimelineRow>(`/api/v1/goodday/${kind}/${id}`, patch);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeline', kind] });
    },
  });
}
